import { ReprocessMessageEventSearch, RsvpFilterEnum, TagFilterEnum } from "@clockwise/schema";
import { isEmpty } from "lodash";
import { GQLAttendeeFilter } from "../../components/proposal/event-search/filters";
import { GQLEventSearchBlock, GQLOrdinalModifier, GQLTimeRangeFilter } from "../../utils/types";
import { EventSearchBlockMap } from "./useUpdateEventSearchFilters";

export const updateOrdinalModiferForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  newValue: GQLOrdinalModifier,
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };

    const newEventSearchBlock = {
      ...eventSearchBlockToChange,
      ordinalModifier: newValue,
    };

    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }
  return originalMap;
};
export const updateTagFilterForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  newValue: TagFilterEnum[],
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };

    const newEventSearchBlock = {
      ...eventSearchBlockToChange,
      tagFilters: newValue,
    };

    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }
  return originalMap;
};

export const udpateRsvpFilterForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  newValue: RsvpFilterEnum[],
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };
    const newEventSearchBlock = {
      ...eventSearchBlockToChange,
      rsvpFilters: newValue,
    };

    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }
  return originalMap;
};

export const updateTitleFilterForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  newValue: GQLEventSearchBlock["titleFilter"] | null,
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };
    const newEventSearchBlock = {
      ...eventSearchBlockToChange,
      titleFilter: newValue,
    };

    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }
  return originalMap;
};

export const updateAttendeeFilterForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  field: Extract<keyof GQLAttendeeFilter, "unresolvedAttendees" | "resolvedAttendees">,
  newValue: string[],
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };

    let newEventSearchBlock: ReprocessMessageEventSearch;
    // If new value is empty, and other data field in filter is also empty, remove the entire filter

    if (
      (field === "unresolvedAttendees" &&
        isEmpty(newValue) &&
        isEmpty(eventSearchBlockToChange?.attendeeFilter?.resolvedAttendees)) ||
      (field === "resolvedAttendees" &&
        isEmpty(newValue) &&
        isEmpty(eventSearchBlockToChange?.attendeeFilter?.unresolvedAttendees))
    ) {
      newEventSearchBlock = {
        ...eventSearchBlockToChange,
        attendeeFilter: null,
      };
    } else {
      newEventSearchBlock = {
        ...eventSearchBlockToChange,
        attendeeFilter: !!eventSearchBlockToChange.attendeeFilter
          ? {
              ...eventSearchBlockToChange.attendeeFilter,
              [field]: newValue,
            }
          : undefined,
      };
    }

    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }
  return originalMap;
};

export const updateTimeRangeFilterForEventSearchId = (
  originalMap: EventSearchBlockMap,
  keyId: string,
  field: Extract<keyof NonNullable<GQLTimeRangeFilter>, "included" | "excluded">,
  newValue: string[],
) => {
  if (originalMap[keyId]) {
    const eventSearchBlockToChange = { ...originalMap[keyId] };
    let newEventSearchBlock: ReprocessMessageEventSearch;
    // If new value is empty, and other data field in filter is also empty, remove the entire filter
    if (
      (field === "included" &&
        isEmpty(newValue) &&
        isEmpty(eventSearchBlockToChange?.timeRangeFilter?.excluded)) ||
      (field === "excluded" &&
        isEmpty(newValue) &&
        isEmpty(eventSearchBlockToChange?.timeRangeFilter?.included))
    ) {
      newEventSearchBlock = {
        ...eventSearchBlockToChange,
        timeRangeFilter: null,
      };
    } else {
      newEventSearchBlock = {
        ...eventSearchBlockToChange,
        timeRangeFilter: !!eventSearchBlockToChange.timeRangeFilter
          ? {
              ...eventSearchBlockToChange.timeRangeFilter,
              [field]: newValue,
            }
          : undefined,
      };
    }
    return {
      ...originalMap,
      [keyId]: newEventSearchBlock,
    };
  }

  return originalMap;
};

export const mapEventSearchBlockToId = (
  eventSearchBlocks: GQLEventSearchBlock[],
): EventSearchBlockMap => {
  return eventSearchBlocks.reduce((acc: EventSearchBlockMap, curr) => {
    const {
      tagFilters,
      attendeeFilter,
      titleFilter,
      rsvpFilters,
      timeRangeFilter,
      ordinalModifier,
    } = curr;
    acc[curr.id] = {
      timeRangeFilter: timeRangeFilter ?? null,
      eventIdFilters: [],
      tagFilters: tagFilters,
      attendeeFilter: !!attendeeFilter
        ? {
            modifier: attendeeFilter.modifier,
            resolvedAttendees: attendeeFilter.resolvedAttendees.map(
              (attendee) => attendee.primaryEmail,
            ),
            unresolvedAttendees: attendeeFilter.unresolvedAttendees,
          }
        : null,
      titleFilter: titleFilter ?? null,
      rsvpFilters: rsvpFilters,
      ordinalModifier: ordinalModifier,
    };
    return acc;
  }, {});
};
